import { Component, Input } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

import { TransferService } from '../transfer.service';
import { ImportReport, ImportStatus, ObjectImportResult, ValidationIssue } from '../transfer.model';

const STATUS_SEVERITIES: Record<ImportStatus, 'success' | 'info' | 'warning' | 'danger'> = {
    CREATED: 'success',
    UPDATED: 'info',
    DUPLICATED: 'info',
    IGNORED: 'warning',
    ERROR: 'danger'
};

const WARNING_KEYS: Record<string, string> = {
    missingDependency: 'tenantTransfer.report.warnings.missingDependency',
    invalidBrevoTemplate: 'tenantTransfer.report.warnings.invalidBrevoTemplate',
    brevoApiKeyMissing: 'tenantTransfer.report.warnings.brevoApiKeyMissing',
    partnerReset: 'tenantTransfer.report.warnings.partnerReset'
};

@Component({
    selector: 'dmp-transfer-report',
    templateUrl: './transfer-report.component.html',
    styleUrls: ['./transfer-report.component.scss'],
    standalone: false
})
export class TransferReportComponent {
    @Input() report: ImportReport;
    @Input() sourceTenant: string;

    duplicatingTemplateId: number = null;

    constructor(
        private transferService: TransferService,
        private messageService: MessageService,
        private readonly translate: TranslateService
    ) {}

    get counts(): Partial<Record<ImportStatus, number>> {
        const counts: Partial<Record<ImportStatus, number>> = {};
        for (const result of this.report?.results || []) {
            counts[result.status] = (counts[result.status] || 0) + 1;
        }
        return counts;
    }

    typeLabel(type: string): string {
        return this.translate.instant('tenantTransfer.types.' + type) || type;
    }

    statusLabel(status: ImportStatus): string {
        return this.translate.instant('tenantTransfer.report.statuses.' + status) || status;
    }

    statusSeverity(status: ImportStatus): 'success' | 'info' | 'warning' | 'danger' {
        return STATUS_SEVERITIES[status] || 'info';
    }

    issuesFor(result: ObjectImportResult): ValidationIssue[] {
        return result.issues || [];
    }

    warningLabel(issue: ValidationIssue): string {
        const key = WARNING_KEYS[issue.code];
        return key ? this.translate.instant(key) : issue.message;
    }

    /** Brevo template ids are numeric but travel as strings in EmailEventStepDTO.templateId. */
    extractBrevoTemplateId(message: string): number | null {
        const match = message.match(/template Brevo (\d+)/);
        return match ? Number(match[1]) : null;
    }

    duplicateBrevoTemplate(issue: ValidationIssue): void {
        const templateId = this.extractBrevoTemplateId(issue.message);
        if (!templateId || !this.report) {
            return;
        }
        this.duplicatingTemplateId = templateId;
        this.transferService
            .duplicateBrevoTemplate(this.report.targetTenant, this.sourceTenant || this.report.sourceTenant, templateId)
            .subscribe({
                next: result => {
                    this.duplicatingTemplateId = null;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('tenantTransfer.report.brevoDuplicatedSummary'),
                        detail: this.translate.instant('tenantTransfer.report.brevoDuplicatedDetail',
                            {id: result.templateId, name: issue.ref.name || issue.ref.code}),
                        life: 10000
                    });
                },
                error: () => {
                    this.duplicatingTemplateId = null;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('components.dyn-raw-detail-display.errorSummary'),
                        detail: this.translate.instant('tenantTransfer.report.brevoDuplicateErrorDetail')
                    });
                }
            });
    }
}
